import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

import { toggleWatchedListing } from '../../ducks/watchedListings.duck';
import { useIntl } from '../../util/reactIntl';
import {
  WATCH_LIMIT_REACHED,
  isListingWatched,
  toListingIdString,
} from '../../util/watchedListings';

import { InlineTextButton } from '../Button/Button';
import IconHeart from '../IconHeart/IconHeart';

import css from './WatchButton.module.css';

/**
 * Watch / unwatch control for a listing.
 * Hidden for guests and for the listing author.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.listing - Listing entity (needs id + author)
 * @param {'icon'|'text'} [props.variant='icon'] - Card heart vs listing-page text button
 * @param {string} [props.className]
 * @param {string} [props.rootClassName]
 * @returns {JSX.Element|null}
 */
const WatchButton = props => {
  const { listing, variant = 'icon', className, rootClassName } = props;
  const intl = useIntl();
  const dispatch = useDispatch();
  const currentUser = useSelector(state => state.user.currentUser);
  const toggleInProgress = useSelector(state => state.watchedListings?.toggleInProgress);
  const togglingListingId = useSelector(state => state.watchedListings?.togglingListingId);
  const [limitError, setLimitError] = useState(false);

  const listingIdStr = toListingIdString(listing?.id);
  const authorId = toListingIdString(listing?.author?.id);
  const currentUserId = toListingIdString(currentUser?.id);

  if (!currentUser || !listingIdStr || (authorId && authorId === currentUserId)) {
    return null;
  }

  const isWatched = isListingWatched(currentUser, listingIdStr);
  // Serialize all watch toggles — concurrent updateProfile calls can clobber privateData.
  const isAnyToggleInProgress = Boolean(toggleInProgress);
  // Only the in-flight listing uses native disabled (opacity wait style).
  const isThisListingToggling = isAnyToggleInProgress && togglingListingId === listingIdStr;
  // Rare: in-progress with no id — disable all so clicks aren't silently ignored.
  const showDisabledWait = isThisListingToggling || (isAnyToggleInProgress && !togglingListingId);
  // Other hearts: block interaction without the opacity flash.
  const isBlockedByOtherToggle = isAnyToggleInProgress && !showDisabledWait;

  const onToggle = event => {
    event.preventDefault();
    event.stopPropagation();
    if (isAnyToggleInProgress) {
      return;
    }
    setLimitError(false);

    return dispatch(toggleWatchedListing(listing)).catch(err => {
      if (err?.type === WATCH_LIMIT_REACHED) {
        setLimitError(true);
      }
    });
  };

  const ariaLabel = isWatched
    ? intl.formatMessage({ id: 'WatchButton.unwatchAriaLabel' })
    : intl.formatMessage({ id: 'WatchButton.watchAriaLabel' });

  const limitMessage = limitError ? (
    <span className={css.limitError} role="alert">
      {intl.formatMessage({ id: 'WatchButton.limitReached' })}
    </span>
  ) : null;

  if (variant === 'text') {
    return (
      <div className={classNames(rootClassName || css.textRoot, className)}>
        <InlineTextButton
          className={classNames(css.textButton, {
            [css.textButtonWatched]: isWatched,
            [css.textButtonBusy]: isBlockedByOtherToggle,
          })}
          onClick={onToggle}
          type="button"
          disabled={showDisabledWait}
          aria-disabled={isBlockedByOtherToggle || undefined}
        >
          <IconHeart className={css.textIcon} isFilled={isWatched} isAriaHidden />
          <span>
            {isWatched
              ? intl.formatMessage({ id: 'WatchButton.watching' })
              : intl.formatMessage({ id: 'WatchButton.watchListing' })}
          </span>
        </InlineTextButton>
        {limitMessage}
      </div>
    );
  }

  return (
    <div className={classNames(rootClassName || css.iconRoot, className)}>
      <button
        type="button"
        className={classNames(css.iconButton, {
          [css.iconButtonWatched]: isWatched,
          [css.iconButtonBusy]: isBlockedByOtherToggle,
        })}
        onClick={onToggle}
        aria-label={ariaLabel}
        aria-pressed={isWatched}
        aria-disabled={isBlockedByOtherToggle || undefined}
        disabled={showDisabledWait}
      >
        <IconHeart className={css.icon} isFilled={isWatched} isAriaHidden />
      </button>
      {limitMessage}
    </div>
  );
};

export default WatchButton;
